import { isEmpty } from 'lodash'

import { formatAIContentToHTML } from 'lib/formatters'

const fallbackCopyTextToClipboard = (text, isHTML = false) => {
  const textArea = document.createElement(isHTML ? 'div' : 'textarea')

  if (isHTML) {
    textArea.innerHTML = text
  } else {
    textArea.value = text
    textArea.style.width = '2em'
    textArea.style.height = '2em'
  }

  textArea.style.position = 'fixed'
  textArea.style.top = 0
  textArea.style.left = 0
  textArea.style.padding = 0
  textArea.style.border = 'none'
  textArea.style.outline = 'none'
  textArea.style.boxShadow = 'none'
  textArea.style.background = 'transparent'

  document.body.appendChild(textArea)

  if (!isHTML) {
    textArea.focus()
    textArea.select()
  } else {
    const range = document.createRange()
    range.selectNodeContents(textArea)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  try {
    document.execCommand('copy')
  } catch (error) {
    console.error('Fallback: Oops, unable to copy', error)
  } finally {
    document.body.removeChild(textArea)
  }
}

const copyTextToClipBoard = (text, isHTML = false) => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text, isHTML)
    return
  }

  if (isHTML) {
    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([text], { type: 'text/html' })
        })
      ])
      .catch(error => {
        console.error('Async: Could not copy HTML: ', error)
      })
  } else {
    navigator.clipboard.writeText(text).catch(error => {
      console.error('Async: Could not copy text: ', error)
    })
  }
}

export const parseHTMLStringToText = htmlString => {
  if (typeof htmlString !== 'string') return ''

  const parsedNote = new DOMParser().parseFromString(htmlString, 'text/html')

  return parsedNote.body.textContent || ''
}

const replaceEmptyParagraphsWithNbsp = html =>
  html
    .replace(/<p>\s*(?:<br[^>]*>\s*)+<\/p>/gi, '<p>&nbsp;</p>')
    .replaceAll(/<br-o>/g, '')

function extractPlainTextWithTags(html) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  let plainText = ''
  let listCounter = 1 // Counter for ordered lists

  function processNode(node, isOrderedList = false) {
    if (node.nodeType === 3) {
      // If it's a text node, append its content
      plainText += node.textContent.trim()
    } else if (node.nodeType === 1) {
      switch (node.tagName) {
        case 'DIV':
          if (node.childNodes.length > 0) {
            node.childNodes.forEach(child => processNode(child))
          } else {
            plainText += `${node.textContent.trim()}\n`
          }
          break
        case 'P':
          if (isEmpty(node.textContent)) {
            if (node.childNodes.length > 0) {
              node.childNodes.forEach(child => processNode(child))
            }
          } else {
            plainText += `${node.textContent.trim()}\n`
          }
          break
        case 'BR':
          plainText += '\n'
          break
        case 'BLOCKQUOTE':
          plainText += `\n> ${node.textContent.trim()}\n`
          break
        case 'CODE':
          plainText += `\n\`\`\`\n${node.textContent.trim()}\n\`\`\`\n`
          break
        case 'EM':
          plainText += `*${node.textContent.trim()}*`
          break
        case 'UL': // Unordered list
          node.childNodes.forEach(li => processNode(li, false))
          break
        case 'OL': // Ordered list
          listCounter = 1 // Reset counter for ordered lists
          node.childNodes.forEach(li => processNode(li, true))
          break
        case 'LI': // List item
          if (isOrderedList) {
            plainText += `${listCounter}. ${node.textContent.trim()}\n`
            // eslint-disable-next-line no-plusplus
            listCounter++
          } else {
            plainText += `- ${node.textContent.trim()}\n`
          }
          break
        default:
          plainText += node.textContent.trim()
          break
      }
    }
  }

  tempDiv.childNodes.forEach(node => processNode(node))

  return plainText.trim()
}

const buildContentToCopy = (isHTML, section, content, withHeader) => {
  let header = ''
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content

  if (withHeader) {
    if (isHTML) {
      const appendExtraBRToHeader =
        tempDiv.firstChild?.nodeType === 3 ||
        tempDiv.firstChild?.nodeName === 'DIV'
          ? '<br>'
          : ''
      header = `<strong>${section}</strong><br>${appendExtraBRToHeader}`
    } else {
      header = `${section}\n\n`
    }
  }

  let body = ''
  if (isHTML) {
    const appendExtraBRToContent =
      tempDiv.lastChild?.nodeType === 3 ||
      tempDiv.lastChild?.nodeName === 'DIV' ||
      tempDiv.textContent.trim() === ''
        ? '<br>'
        : ''

    // While doing RTF copy, we need to replace empty paragraphs with &nbsp;
    // since some editors collapse <p> tags with <br> tags in them and don't treat them as
    // empty paragraphs
    body = `${replaceEmptyParagraphsWithNbsp(content)}${appendExtraBRToContent}`
  } else {
    body = extractPlainTextWithTags(content.replaceAll(/<br-o>/g, '<br>'))
  }

  return header + body
}

export const copyAINoteContent = (notes, options = {}) => {
  if (!notes || notes.length === 0) return

  const { withHeader = false, isHTML = false } = options

  const formattedContent = notes
    .map(note => {
      const section = note.section?.trim() || ''
      const content = `${formatAIContentToHTML(note.content)}`
      return buildContentToCopy(isHTML, section, content, withHeader)
    })
    .join(isHTML ? '' : '\n\n')

  copyTextToClipBoard(formattedContent, isHTML)
}

export default copyTextToClipBoard
